import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import { BudgetTypeCode, PaymentTermsCode, ServiceTypeCode } from "../../models/models";
import { getUiLocale } from "../../../../shared/utils/relativeTime";
import { formatUiDate, formatUiDateRange } from "../../../../shared/utils/dateFormat";

interface RfsCommercialTermsCardProps {
  sr: PublicServiceRequest;
}

/** Single term row */
const TermRow = ({
  label,
  value,
  isLast,
}: {
  label: string;
  value: string;
  isLast?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: "10px",
      borderBottom: isLast ? "none" : "1px solid #F3F4F6",
      gap: 2,
    }}
  >
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        fontFamily: dt.font,
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: dt.font,
        color: "#1E293B",
        textAlign: "right",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Box>
  </Box>
);

const RfsCommercialTermsCard = ({ sr }: RfsCommercialTermsCardProps) => {
  const { translate } = useTranslation(["servicing", "serviceBoard"]);

  // Service date
  const getServiceDateLabel = (): string => {
    if (sr.flexibleDates) return translate("serviceBoard:serviceDateAsap");
    if (!sr.serviceStartDate && !sr.serviceEndDate) return translate("servicing:serviceDateNotSet");
    if (sr.serviceStartDate) {
      if (sr.serviceEndDate) return formatUiDateRange(sr.serviceStartDate, sr.serviceEndDate, " - ");
      return formatUiDate(sr.serviceStartDate);
    }
    if (sr.serviceEndDate) return formatUiDate(sr.serviceEndDate);
    return translate("servicing:serviceDateNotSet");
  };

  // Working hours
  const getWorkingHoursLabel = (): string => {
    if (!sr.workingHoursFrom || !sr.workingHoursTo) return translate("servicing:workingHoursNotSet");
    const from = sr.workingHoursFrom.substring(0, 5);
    const to = sr.workingHoursTo.substring(0, 5);
    return `${from} \u2014 ${to}`;
  };

  // Weekend work
  const getWeekendWorkLabel = (): string => {
    return sr.weekendWorkPossible
      ? translate("servicing:available")
      : translate("servicing:notAvailable");
  };

  // Budget
  const getBudgetLabel = (): string => {
    if (!sr.budgetTypeCode) return translate("servicing:budgetTypeCodes.requestQuote");
    switch (sr.budgetTypeCode) {
      case BudgetTypeCode.fixed:
        return sr.fixedBudget ? `\u20AC${sr.fixedBudget.toLocaleString(getUiLocale())}` : "\u2014";
      case BudgetTypeCode.range:
        if (sr.budgetRangeFrom && sr.budgetRangeTo) {
          return `\u20AC${sr.budgetRangeFrom.toLocaleString(getUiLocale())} \u2014 \u20AC${sr.budgetRangeTo.toLocaleString(getUiLocale())}`;
        }
        return "\u2014";
      case BudgetTypeCode.requestQuote:
        return translate("servicing:budgetTypeCodes.requestQuote");
      default:
        return "\u2014";
    }
  };

  // Payment terms
  const getPaymentTermsLabel = (): string => {
    if (!sr.paymentTermsCode) return translate("servicing:paymentTermsNotSet");
    const map: Record<number, string> = {
      [PaymentTermsCode.net30Days]: translate("servicing:paymentTermsCodes.net30Days"),
      [PaymentTermsCode.net60Days]: translate("servicing:paymentTermsCodes.net60Days"),
      [PaymentTermsCode.net90Days]: translate("servicing:paymentTermsCodes.net90Days"),
      [PaymentTermsCode.dueOnReceipt]: translate("servicing:paymentTermsCodes.dueOnReceipt"),
      [PaymentTermsCode.upfront50Completion]: translate("servicing:paymentTermsCodes.upfront50Completion"),
    };
    return map[sr.paymentTermsCode] || translate("servicing:paymentTermsNotSet");
  };

  const terms = [
    { label: translate("servicing:serviceDate"), value: getServiceDateLabel() },
    { label: translate("servicing:workingHours"), value: getWorkingHoursLabel() },
    { label: translate("servicing:weekendWork"), value: getWeekendWorkLabel() },
    { label: translate("servicing:budget"), value: getBudgetLabel() },
    { label: translate("servicing:paymentTerms"), value: getPaymentTermsLabel() },
  ];

  return (
    <Box>
      {/* Section header */}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          fontFamily: dt.font,
          textTransform: "uppercase",
          letterSpacing: "1.2px",
          color: dt.textMuted,
          mb: "10px",
        }}
      >
        {translate("servicing:commercialTermsAndConditions")}
      </Box>

      {/* Terms card */}
      <Box
        sx={{
          backgroundColor: "#F8FAFC",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        {terms.map((term, index) => (
          <TermRow
            key={term.label}
            label={term.label}
            value={term.value}
            isLast={index === terms.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

export default RfsCommercialTermsCard;
